import '../tasks/task.css';
import { AutoComplete, Button, Form, Input, InputNumber, TreeSelect } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { serverFetch } from '@server';
import { showError } from '@extensions/utils';
import ResizableDrawer from '@controls/resizable-drawer/resizable-drawer';

export const NO_GROUP = -1;

// Карточка товара в Drawer. product.id = 0 — новый.
// onSaved(product, isNew); «ОК и новый» — сохранить и сразу начать следующий товар в той же группе
const Product = ({ product, groupOptions, units, readOnly, onClose, onSaved }) => {
    const [form] = Form.useForm();
    const [dirty, setDirty] = useState(false);
    const [current, setCurrent] = useState(product);
    const [saving, setSaving] = useState(false);
    const nameRef = useRef(null);
    const isNew = !current.id;

    useEffect(() => {
        form.resetFields();
        form.setFieldsValue({ ...current, groupId: current.groupId ?? NO_GROUP });
        setDirty(false);
        setTimeout(() => nameRef.current?.focus(), 0);
    }, [current, form]);

    const save = (andNew) => {
        form.validateFields().then((values) => {
            const body = {
                ...current,
                ...values,
                name: values.name.trim(),
                unit: (values.unit ?? '').trim(),
                description: values.description ?? '',
                price: values.price ?? 0,
                groupId: values.groupId === NO_GROUP ? null : values.groupId,
            };
            setSaving(true);
            serverFetch(isNew ? 'product' : `product/${current.id}`, { method: isNew ? 'POST' : 'PUT', bodyData: body })
                .then((saved) => {
                    onSaved?.(saved, isNew);
                    if (andNew) {
                        setCurrent({ id: 0, name: '', unit: body.unit, price: 0, description: '', groupId: body.groupId });
                    } else {
                        onClose?.();
                    }
                })
                .catch(showError)
                .finally(() => setSaving(false));
        });
    };

    const unitOptions = units.map((u) => ({ value: u }));

    return (
        <ResizableDrawer
            open
            storageKey="productDrawerWidth"
            defaultWidth={520}
            title={isNew ? 'Новый товар' : 'Товар'}
            onClose={() => onClose?.()}
            destroyOnClose
            className="resizable-drawer task-drawer product-drawer"
        >
            <Form
                form={form}
                layout="vertical"
                disabled={readOnly}
                onFinish={() => save(false)}
                onValuesChange={() => setDirty(true)}
                onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); form.submit(); }
                }}
            >
                <div className="task-scroll">
                    <div className="task-section">
                        <Form.Item
                            name="name"
                            label="Наименование"
                            rules={[{ required: true, whitespace: true, message: 'Укажите наименование' }]}
                        >
                            <Input variant="filled" size="large" placeholder="Наименование товара" ref={nameRef} />
                        </Form.Item>
                        <div className="product-form-row">
                            <Form.Item name="price" label="Цена" className="product-form-price">
                                <InputNumber
                                    variant="filled"
                                    min={0}
                                    precision={2}
                                    decimalSeparator=","
                                    style={{ width: '100%' }}
                                    onFocus={(e) => e.target.select()}
                                />
                            </Form.Item>
                            <Form.Item name="unit" label="Ед. изм." className="product-form-unit">
                                <AutoComplete
                                    variant="filled"
                                    options={unitOptions}
                                    placeholder="шт"
                                    filterOption={(input, o) => o.value.toLowerCase().startsWith(input.toLowerCase())}
                                />
                            </Form.Item>
                        </div>
                        <Form.Item name="groupId" label="Группа">
                            <TreeSelect
                                variant="filled"
                                showSearch
                                treeDefaultExpandAll
                                treeNodeFilterProp="title"
                                treeData={groupOptions}
                            />
                        </Form.Item>
                        <Form.Item name="description" label="Описание">
                            <Input.TextArea variant="filled" autoSize={{ minRows: 3, maxRows: 12 }} />
                        </Form.Item>
                    </div>
                    <div className="task-actions">
                        {!readOnly && (
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                className="task-ok"
                                loading={saving}
                                disabled={!dirty}
                            >
                                ОК
                            </Button>
                        )}
                        {!readOnly && isNew && (
                            <Button size="large" disabled={!dirty || saving} onClick={() => save(true)}>ОК и новый</Button>
                        )}
                        {!readOnly && isNew && <span className="task-actions-hint">Ctrl+Enter — сохранить</span>}
                        <Button size="large" style={{ marginLeft: 'auto' }} onClick={() => onClose?.()}>Отмена</Button>
                    </div>
                </div>
            </Form>
        </ResizableDrawer>
    );
};

export default Product;
